<template>
    <div id="scheduled-messages">
        <base-table
            :columns="columns"
            :items="plannedList"
            :loading="loading"
            :empty-text="$t('no-messages-in-section')"
            fill
            :fill-offset="88"
        >
            <template #cell(name)="{ item }">
                {{ item.attributes.name }}
            </template>
            <template #cell(content)="{ item }">
                {{ item.attributes.content }}
            </template>
            <template #cell(audiences)="{ item }">
                <span v-if="item.attributes.audiences === null || item.attributes.audiences.length === 0">{{ $t('all') }}</span>
                <template v-else>
                    <div
                        v-for="(tag, index) in item.attributes.audiences"
                        :key="`${tag}-${index}`"
                        class="d-inline-flex p-0 mr-2 tags"
                    >
                        <p>{{ tag }}</p>
                    </div>
                </template>
            </template>
            <template #cell(planned)="{ item }">
                <template v-if="item.attributes.plannedDate || item.attributes.plannedTime">
                    <div class="cell-datetime">
                        {{ fromUtc(`${item.attributes.plannedDate} ${item.attributes.plannedTime}`).date }}
                        {{ fromUtc(`${item.attributes.plannedDate} ${item.attributes.plannedTime}`).time }}
                        <timezone></timezone>
                    </div>
                </template>
                <span v-else>---</span>
            </template>
            <template #cell(actions)="{ item }">
                <button type="button" class="base-table-action" :aria-label="$t('send-now')" @click="sendMassMessage(item.id)">
                    <font-awesome-icon class="text-dark-pink" :icon="['fas', 'paper-plane']"/>
                </button>
                <button type="button" class="base-table-action" :aria-label="$t('edit')" @click="editMassMessage(item)">
                    <font-awesome-icon :icon="['fas', 'pencil-alt']"/>
                </button>
                <button type="button" class="base-table-action base-table-action-danger" :aria-label="$t('delete')" @click="deleteMassMessage(item.id)">
                    <font-awesome-icon :icon="['fas', 'times']" />
                </button>
            </template>
        </base-table>
        <b-pagination
            v-if="massScheduledMeta.page && massScheduledMeta.page.total > massScheduledMeta.page['per-page']"
            v-model="page.number"
            :total-rows="massScheduledMeta.page.total"
            :per-page="massScheduledMeta.page['per-page']"
            @input="loadMassMessages"
            align="center"
        ></b-pagination>
        <send-modal
            :editMessage="editMessage"
            @massListUpdated="loadMassMessages()"
        >
        </send-modal>
    </div>
</template>

<script>

// Internal repository
import SendModal            from './SendModal.vue';
import Alert                from '@/helpers/Alert.js';
import BaseTable            from '@/core/components/BaseTable.vue';
import Timezone             from '@/core/components/Timezone.vue';

// Import external libraries
import { mapActions, mapState } from 'vuex';
import Format from '@/helpers/Format.js';

export default {
    components: {
        SendModal,
        BaseTable,
        Timezone
    },
    data () {
        const MASS_CATEGORY  = 'mass';

        return {
            MASS_CATEGORY,
            loading:     false,
            editMessage: false,
            page:        {
                number: 1,
                size:   25
            }
        };
    },

    computed: {
        ...mapState('core/scheduled-messages', {
            'massMessagesPlanned': 'plannedScheduledMessages',
            'massScheduledMeta':   'plannedScheduledMeta'
        }),

        plannedList () {
            return Object.values(this.massMessagesPlanned);
        },

        columns () {
            return [
                {
                    key:     'name',
                    label:   this.$t('sending-name'),
                    thStyle: { width: '16%' },
                    tdClass: 'cell-ellipsis'
                },
                {
                    key:     'content',
                    label:   'Messages',
                    thStyle: { width: '30%' },
                    tdClass: 'cell-ellipsis'
                },
                {
                    key:     'audiences',
                    label:   this.$t('audience'),
                    thStyle: { width: '22%' }
                },
                {
                    key:     'planned',
                    label:   this.$t('schedule-diffusion'),
                    thStyle: { width: '22%' }
                },
                {
                    key:     'actions',
                    label:   '',
                    thStyle: { width: '120px' },
                    tdClass: 'cell-actions'
                }
            ];
        }
    },

    mounted () {
        this.loadMassMessages();
    },

    methods: {
        ...mapActions('core/scheduled-messages', {
            delete:                   'delete',
            indexPlanned:             'indexPlanned',
            indexSent:                'indexSent',
            selectMassMessage:        'select',
            send:                     'send',
            resetSelectedMassMessage: 'resetSelectedScheduledMessage'
        }),

        /**
         * Load planned and not_planned mass messages
         */
        loadMassMessages () {
            this.loading = true;

            // Get list of massive planned messages
            this.indexPlanned({
                'category': this.MASS_CATEGORY,
                page:       this.page
            })
                .then(() => {
                    // Finish loading
                    this.loading = false;
                });

            // Get list of massive sent messages
            this.indexSent({
                'category': this.MASS_CATEGORY,
                page:       this.page
            });
        },

        /**
         * Add massive message
         */
        addMassMessage () {
            this.resetSelectedMassMessage();
            this.editMessage = false;
            this.$bvModal.show('massive-send-sidebar');
        },

        /**
         * Edit a selected massive message
         */
        editMassMessage (massMessage) {
            this.editMessage = true;
            this.selectMassMessage({ id: massMessage.id, status: 'planned' })
                .then(() => {
                    this.$bvModal.show('massive-send-sidebar');
                });
        },

        /**
         * Send immediately a massive message
         */
        sendMassMessage (id) {
            this.$dialog.confirm({
                title:       this.$t('title-mass-message-send'),
                text:        this.$t('confirm-scheduled-send'),
                centerText:  true,
                image:       '/images/confirm-send-scheduled-message.png',
                confirmText: this.$t('send-scheduled-message'),
                onConfirm:   () => this.send({ id })
            })
                .then((confirmed) => {
                    if (confirmed) {
                        Alert.success(this.$i18n.t('scheduled-send-success'));
                    }
                })
                .catch(() => {
                    Alert.fail(this.$i18n.t('default-error'));
                });
        },

        /**
         * Delete a selected massive message
         */
        deleteMassMessage (id) {
            this.$dialog.confirm({
                title:       this.$t('title-mass-message-delete'),
                text:        this.$t('confirm-mass-message-delete'),
                centerText:  true,
                confirmText: this.$t('delete'),
                danger:      true,
                onConfirm:   () => this.delete({ id })
            })
                .then((confirmed) => {
                    if (confirmed) {
                        Alert.success(this.$i18n.t('scheduled-deleted-success'));
                    }
                })
                .catch(() => {
                    Alert.fail(this.$i18n.t('default-error'));
                });
        },

        /**
         * Helper method to format date to use in template
         */
        fromUtc: Format.fromUtc
    }
};
</script>
